import React, { FC, useMemo } from 'react'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { Box, Typography } from '@mui/material'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { PORTAL_TEXT } from '@/apps/latest/shared/styles/portalTypography'
import { RADIUS } from '@/shared/styles/radii'

// Notion 基調の chart palette. accent.primary を主軸に、 補助系列は fg.muted/faint.
// 状態色 (success/warn/danger) は「状態を示す」専用で chart default に出さない.
const NOTION_CHART_COLORS = [
  colorTokens.accent.primary,
  colorTokens.fg.muted,
  colorTokens.fg.faint,
  colorTokens.accent.primaryHover,
  colorTokens.fg.disabled,
]

// Notion 流 tooltip wrapper sx (= 白い紙 + subtle border + popover shadow).
const TOOLTIP_WRAPPER_SX = {
  backgroundColor: colorTokens.bg.default,
  border: `1px solid ${colorTokens.border.subtle}`,
  borderRadius: `${RADIUS.md}px`,
  p: 1.25,
  boxShadow: colorTokens.shadow.cardSubtle,
} as const

const TOOLTIP_CONTENT_STYLE = {
  backgroundColor: colorTokens.bg.default,
  border: `1px solid ${colorTokens.border.subtle}`,
  borderRadius: RADIUS.sm,
}

const AXIS_TICK_STYLE = { fontSize: 12, fill: colorTokens.fg.muted }

interface BaseChartProps {
  title?: string
  height?: number
}

interface LineChartProps extends BaseChartProps {
  data: Array<Record<string, string | number>>
  dataKey?: string
  dataKeys?: string[]
  xAxisKey: string
  color?: string
  colors?: string[]
  yAxisTickFormatter?: (value: number) => string
  yAxisUnit?: string // Y軸の単位
}

interface BarChartProps extends BaseChartProps {
  data: Array<Record<string, string | number>>
  dataKey: string
  xAxisKey: string
  color?: string
  yAxisTickFormatter?: (value: number) => string
}

interface ChartOrderOptions {
  graphStackBottom?: 'first' | 'last'
  legendReverse?: boolean
  tooltipReverse?: boolean
}

interface StackedBarChartProps extends BaseChartProps {
  data: Array<Record<string, string | number>>
  dataKeys: string[]
  xAxisKey: string
  colors?: string[]
  graphStackBottom?: 'first' | 'last' // dataKeysの最初/最後どちらを下にするか
  legendReverse?: boolean // 凡例を逆順にするか
  tooltipReverse?: boolean // ツールチップを逆順にするか
  yAxisTickFormatter?: (value: number) => string
  yAxisUnit?: string // Y軸の単位
}

interface ScatterChartProps extends BaseChartProps {
  data: Array<Record<string, string | number>>
  xKey: string
  yKey: string
  color?: string
}

// カスタムフック: グラフの表示順序制御
const useChartOrdering = (dataKeys: string[], options: ChartOrderOptions) => {
  return useMemo(() => {
    const {
      graphStackBottom = 'first',
      legendReverse = false,
      tooltipReverse = false,
    } = options

    // Bar定義順序の決定（Rechartsの仕様: 最初に定義 = 下）
    const barOrder =
      graphStackBottom === 'first' ? dataKeys : [...dataKeys].reverse()

    // 凡例表示順序の決定
    const legendOrder = legendReverse ? [...dataKeys].reverse() : dataKeys

    // ツールチップ表示順序の決定
    const tooltipOrder = tooltipReverse ? [...dataKeys].reverse() : dataKeys

    return { barOrder, legendOrder, tooltipOrder }
    // Note: 個別のプロパティをトラッキングすることで不要な再レンダリングを防ぐ
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataKeys,
    options.graphStackBottom,
    options.legendReverse,
    options.tooltipReverse,
  ])
}

// Rechartsのペイロードアイテムの型定義
interface PayloadItem {
  dataKey: string
  name: string
  value: string | number
  color: string
}

// カスタムツールチップコンポーネント
interface CustomTooltipProps {
  active?: boolean
  payload?: PayloadItem[]
  label?: string
  tooltipOrder: string[]
  yAxisUnit?: string
}

const CustomTooltip: FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  tooltipOrder,
  yAxisUnit,
}) => {
  if (!active || !payload || !payload.length) return null

  // tooltipOrderに従って並び替え
  const orderedPayload = tooltipOrder
    .map((key) => payload.find((p) => p.dataKey === key))
    .filter((item): item is PayloadItem => item !== undefined)

  return (
    <Box sx={TOOLTIP_WRAPPER_SX}>
      <Typography sx={{ ...PORTAL_TEXT.rowLabel, mb: 0.5 }}>{label}</Typography>
      {orderedPayload.map((entry) => (
        <Typography
          key={entry.dataKey}
          sx={{ ...PORTAL_TEXT.cellSecondary, color: entry.color }}
        >
          {entry.name}: {yAxisUnit ? `${entry.value}${yAxisUnit}` : entry.value}
        </Typography>
      ))}
    </Box>
  )
}

// Rechartsの凡例ペイロードアイテムの型定義
interface LegendPayloadItem {
  value: string
  color: string
}

// カスタム凡例コンポーネント
interface CustomLegendProps {
  payload?: LegendPayloadItem[]
  legendOrder: string[]
}

const CustomLegend: FC<CustomLegendProps> = ({ payload, legendOrder }) => {
  if (!payload) return null

  // legendOrderに従って並び替え
  const orderedPayload = legendOrder
    .map((key) => payload.find((p) => p.value === key))
    .filter((item): item is LegendPayloadItem => item !== undefined)

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
      {orderedPayload.map((entry) => (
        <Box
          key={entry.value}
          sx={{ display: 'flex', alignItems: 'center', mx: 1.5 }}
        >
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: entry.color,
              mr: 0.5,
            }}
          />
          <Typography sx={PORTAL_TEXT.cellSecondary}>{entry.value}</Typography>
        </Box>
      ))}
    </Box>
  )
}

// 時間フォーマット用のヘルパー関数
export const formatDurationToMinutes = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = Math.round(seconds % 60)
  if (mins === 0) {
    return `${secs}秒`
  }
  return secs > 0 ? `${mins}分${secs}秒` : `${mins}分`
}

// 折れ線グラフコンポーネント
export const MetricsLineChart: FC<LineChartProps> = ({
  title,
  data,
  dataKey,
  dataKeys,
  xAxisKey,
  color = colorTokens.accent.primary,
  colors = NOTION_CHART_COLORS,
  height = 300,
  yAxisTickFormatter,
  yAxisUnit,
}) => {
  // 複数ライン対応
  const lineKeys = dataKeys || (dataKey ? [dataKey] : [])
  const lineColors = dataKeys ? colors : [color]

  // Y軸のフォーマット関数
  const defaultYAxisFormatter =
    yAxisTickFormatter ||
    ((value: number) => (yAxisUnit ? `${value}${yAxisUnit}` : value.toString()))
  return (
    <Box>
      {title && (
        <Typography sx={{ ...PORTAL_TEXT.sectionHeading, mb: 2 }}>
          {title}
        </Typography>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={colorTokens.border.subtle}
          />
          <XAxis
            dataKey={xAxisKey}
            tick={AXIS_TICK_STYLE}
            stroke={colorTokens.border.subtle}
          />
          <YAxis
            tick={AXIS_TICK_STYLE}
            stroke={colorTokens.border.subtle}
            tickFormatter={defaultYAxisFormatter}
          />
          <Tooltip
            contentStyle={TOOLTIP_CONTENT_STYLE}
            formatter={(value) =>
              typeof value === 'number' ? defaultYAxisFormatter(value) : value
            }
          />
          <Legend
            wrapperStyle={{ fontSize: 13, color: colorTokens.fg.muted }}
          />
          {lineKeys.map((key, index) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={lineColors[index % lineColors.length]}
              strokeWidth={2}
              dot={{ fill: lineColors[index % lineColors.length], r: 4 }}
              activeDot={{ r: 6 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  )
}

// 棒グラフコンポーネント
export const MetricsBarChart: FC<BarChartProps> = ({
  title,
  data,
  dataKey,
  xAxisKey,
  color = colorTokens.accent.primary,
  height = 300,
  yAxisTickFormatter,
}) => {
  return (
    <Box>
      {title && (
        <Typography sx={{ ...PORTAL_TEXT.sectionHeading, mb: 2 }}>
          {title}
        </Typography>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={colorTokens.border.subtle}
          />
          <XAxis
            dataKey={xAxisKey}
            tick={{ fontSize: 11, fill: colorTokens.fg.muted }}
            stroke={colorTokens.border.subtle}
            interval={0}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis
            tick={AXIS_TICK_STYLE}
            stroke={colorTokens.border.subtle}
            tickFormatter={yAxisTickFormatter}
          />
          <Tooltip
            contentStyle={TOOLTIP_CONTENT_STYLE}
            formatter={(value) =>
              yAxisTickFormatter && typeof value === 'number'
                ? yAxisTickFormatter(value)
                : value
            }
          />
          <Legend
            wrapperStyle={{ fontSize: 13, color: colorTokens.fg.muted }}
          />
          <Bar dataKey={dataKey} fill={color} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  )
}

// 積み上げ棒グラフコンポーネント
export const MetricsStackedBarChart: FC<StackedBarChartProps> = ({
  title,
  data,
  dataKeys,
  xAxisKey,
  colors = [
    colorTokens.accent.primary,
    colorTokens.accent.primary,
    colorTokens.bg.subtle,
  ],
  height = 300,
  graphStackBottom = 'first',
  legendReverse = false,
  tooltipReverse = false,
  yAxisTickFormatter,
  yAxisUnit,
}) => {
  // 順序制御のロジックをカスタムフックから取得
  const { barOrder, legendOrder, tooltipOrder } = useChartOrdering(dataKeys, {
    graphStackBottom,
    legendReverse,
    tooltipReverse,
  })

  // Y軸のフォーマット関数
  const defaultYAxisFormatter =
    yAxisTickFormatter ||
    ((value: number) => (yAxisUnit ? `${value}${yAxisUnit}` : value.toString()))

  return (
    <Box>
      {title && (
        <Typography sx={{ ...PORTAL_TEXT.sectionHeading, mb: 2 }}>
          {title}
        </Typography>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={colorTokens.border.subtle}
          />
          <XAxis
            dataKey={xAxisKey}
            tick={AXIS_TICK_STYLE}
            stroke={colorTokens.border.subtle}
          />
          <YAxis
            tick={AXIS_TICK_STYLE}
            stroke={colorTokens.border.subtle}
            tickFormatter={defaultYAxisFormatter}
          />
          <Tooltip
            content={
              <CustomTooltip
                tooltipOrder={tooltipOrder}
                yAxisUnit={yAxisUnit}
              />
            }
          />
          <Legend content={<CustomLegend legendOrder={legendOrder} />} />
          {barOrder.map((key, index) => {
            const colorIndex = dataKeys.indexOf(key)
            return (
              <Bar
                key={key}
                dataKey={key}
                stackId="metrics"
                fill={colors[colorIndex]}
                radius={
                  index === barOrder.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]
                }
              />
            )
          })}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  )
}

// 散布図コンポーネント
export const MetricsScatterChart: FC<ScatterChartProps> = ({
  title,
  data,
  xKey,
  yKey,
  color = colorTokens.accent.primary,
  height = 300,
}) => {
  return (
    <Box>
      {title && (
        <Typography sx={{ ...PORTAL_TEXT.sectionHeading, mb: 2 }}>
          {title}
        </Typography>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <ScatterChart>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={colorTokens.border.subtle}
          />
          <XAxis
            dataKey={xKey}
            name={xKey}
            tick={AXIS_TICK_STYLE}
            stroke={colorTokens.border.subtle}
            label={{
              value: xKey,
              position: 'insideBottom',
              offset: -5,
              style: { fontSize: 12, fill: colorTokens.fg.muted },
            }}
          />
          <YAxis
            dataKey={yKey}
            name={yKey}
            tick={AXIS_TICK_STYLE}
            stroke={colorTokens.border.subtle}
            label={{
              value: yKey,
              angle: -90,
              position: 'insideLeft',
              style: { fontSize: 12, fill: colorTokens.fg.muted },
            }}
          />
          <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} />
          <Scatter name="データポイント" data={data} fill={color} />
        </ScatterChart>
      </ResponsiveContainer>
    </Box>
  )
}
